import React, { useState } from "react";
import axios from "axios";
import { QUESTIONS, PROGRAMS } from "../data/quizQuestions";
import * as Icons from "lucide-react";
import { Sparkles, ArrowRight, ArrowLeft, RotateCcw, CheckCircle2, Send, User, Phone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Career-match quiz — 20 bilingual questions. Scores answers to determine best
 * program fit. Ends with a mini lead-capture form.
 */
export default function CareerQuiz({ lang, onClose }) {
    const isAr = lang === "ar";

    // 3 modes: quiz | result | form
    const [step, setStep] = useState("intro"); // intro | quiz | result | form | done
    const [current, setCurrent] = useState(0);
    const [scores, setScores] = useState(
        Object.fromEntries(Object.keys(PROGRAMS).map((k) => [k, 0]))
    );
    const [leadForm, setLeadForm] = useState({ fullName: "", phone: "" });
    const [submitting, setSubmitting] = useState(false);

    // Best program based on scores
    const bestProgramId = Object.entries(scores).sort((a, b) => b[1] - a[1])[0]?.[0];
    const bestProgram = PROGRAMS[bestProgramId];

    // Top 3 for the results screen
    const top3 = Object.entries(scores)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 3)
        .map(([id, score]) => ({ id, score, ...PROGRAMS[id] }));

    const handleAnswer = (answer) => {
        const newScores = { ...scores };
        Object.entries(answer.points).forEach(([k, v]) => {
            newScores[k] = (newScores[k] || 0) + v;
        });
        setScores(newScores);
        if (current < QUESTIONS.length - 1) {
            setCurrent(current + 1);
        } else {
            setStep("result");
        }
    };

    const handleReset = () => {
        setStep("intro");
        setCurrent(0);
        setScores(Object.fromEntries(Object.keys(PROGRAMS).map((k) => [k, 0])));
        setLeadForm({ fullName: "", phone: "" });
    };

    const handleSubmitLead = async (e) => {
        e.preventDefault();
        if (!leadForm.fullName.trim() || !leadForm.phone.trim()) return;
        setSubmitting(true);
        try {
            await axios.post(`${API}/leads`, {
                fullName: leadForm.fullName,
                phone: leadForm.phone,
                preferredCourse: bestProgramId,
                preferredLanguage: lang,
                interestStatus: "hot",
                rawInputChannel: "Web Chat",
                notes: `Career quiz result: ${bestProgram.nameHe} (${bestProgram.nameAr}) · Score: ${scores[bestProgramId]}`,
            });
            setStep("done");
        } catch {
            // silent — could show inline error
        } finally {
            setSubmitting(false);
        }
    };

    // Progress percentage
    const progress = step === "quiz" ? ((current + 1) / QUESTIONS.length) * 100 : 0;
    const q = QUESTIONS[current];
    const ResultIcon = bestProgram ? Icons[bestProgram.icon] || Icons.GraduationCap : Icons.GraduationCap;

    return (
        <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 md:p-8" onClick={onClose} data-testid="career-quiz-modal">
            <div
                className="bg-white rounded-3xl shadow-[0_30px_80px_rgba(0,0,0,0.4)] w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header bar */}
                <div className="bg-gradient-to-r from-[#0B1F3D] to-[#1A365D] text-white px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Sparkles size={18} className="text-[#F8D89C]" />
                        <div className="font-serif font-bold text-lg">
                            {isAr ? "اختبار تحديد المسار" : "מבחן התאמה למסלול"}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        data-testid="quiz-close-btn"
                        className="text-white/70 hover:text-white text-2xl leading-none"
                    >
                        ×
                    </button>
                </div>

                {/* Progress bar */}
                {step === "quiz" && (
                    <div className="h-1.5 bg-slate-100 shrink-0">
                        <div
                            className="h-full bg-gradient-to-r from-[#D4761F] to-[#E08A30] transition-all duration-500"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                )}

                {/* Body — scrollable */}
                <div className="flex-1 overflow-y-auto p-6 md:p-8">
                    {/* Intro */}
                    {step === "intro" && (
                        <div className="text-center py-6" data-testid="quiz-intro">
                            <div className="w-20 h-20 mx-auto rounded-2xl bg-gradient-to-br from-[#0B1F3D] to-[#1A365D] flex items-center justify-center mb-4 shadow-[0_10px_30px_rgba(11,31,61,0.3)]">
                                <Sparkles size={38} className="text-[#F8D89C]" />
                            </div>
                            <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-3">
                                {isAr ? "اكتشف مسارك الأمثل في 3 دقائق" : "מצא את המסלול המתאים לך תוך 3 דקות"}
                            </h3>
                            <p className="text-[#475569] max-w-md mx-auto mb-6">
                                {isAr
                                    ? "20 سؤالًا قصيرًا · نتيجة مخصصة · بدون تعبئة أي معلومات شخصية للبدء."
                                    : "20 שאלות קצרות · תוצאה מדויקת · ללא הזנת פרטים כדי להתחיל."}
                            </p>
                            <ul className="text-sm text-[#475569] mb-8 space-y-2 max-w-xs mx-auto text-start">
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 size={15} className="text-[#22C55E] shrink-0" />
                                    {isAr ? "بحسب ميولك واهتماماتك الحقيقية" : "לפי הנטיות והתחומים האמיתיים שלך"}
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 size={15} className="text-[#22C55E] shrink-0" />
                                    {isAr ? "توصية بأفضل 3 مسارات لك" : "המלצה על 3 המסלולים המתאימים ביותר"}
                                </li>
                                <li className="flex items-center gap-2">
                                    <CheckCircle2 size={15} className="text-[#22C55E] shrink-0" />
                                    {isAr ? "استشارة شخصية مجانية بعد النتيجة" : "ייעוץ אישי חינם אחרי התוצאה"}
                                </li>
                            </ul>
                            <button
                                onClick={() => setStep("quiz")}
                                data-testid="quiz-start-btn"
                                className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4761F] to-[#E08A30] hover:from-[#B36318] hover:to-[#D4761F] text-white px-8 py-3.5 rounded-xl font-bold shadow-[0_10px_30px_rgba(212,118,31,0.4)] transition-all hover:scale-105"
                            >
                                {isAr ? "ابدأ الاختبار" : "התחל מבחן"}
                                {isAr ? <ArrowLeft size={18} /> : <ArrowRight size={18} />}
                            </button>
                        </div>
                    )}

                    {/* Quiz question */}
                    {step === "quiz" && q && (
                        <div data-testid={`quiz-question-${q.id}`}>
                            <div className="text-[11px] font-bold text-[#B36318] uppercase tracking-[0.2em] mb-2">
                                {isAr ? `سؤال ${current + 1} من ${QUESTIONS.length}` : `שאלה ${current + 1} מתוך ${QUESTIONS.length}`}
                            </div>
                            <h3 className="font-serif text-xl md:text-2xl font-bold text-[#0B1F3D] mb-6 leading-tight">
                                {isAr ? q.ar : q.he}
                            </h3>
                            <div className="grid grid-cols-1 gap-3">
                                {q.answers.map((a, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleAnswer(a)}
                                        data-testid={`quiz-answer-${q.id}-${idx}`}
                                        className="group text-start bg-slate-50 hover:bg-gradient-to-r hover:from-[#0B1F3D] hover:to-[#1A365D] hover:text-white border border-slate-200 hover:border-[#0B1F3D] rounded-xl px-5 py-4 transition-all hover:-translate-y-0.5 hover:shadow-md"
                                    >
                                        <span className="text-sm md:text-base text-[#334155] group-hover:text-white transition">
                                            {isAr ? a.ar : a.he}
                                        </span>
                                    </button>
                                ))}
                            </div>
                            {current > 0 && (
                                <button
                                    onClick={() => setCurrent(current - 1)}
                                    className="mt-6 text-[#475569] hover:text-[#0B1F3D] text-sm font-semibold inline-flex items-center gap-1.5"
                                >
                                    {isAr ? <ArrowRight size={14} /> : <ArrowLeft size={14} />}
                                    {isAr ? "السؤال السابق" : "שאלה קודמת"}
                                </button>
                            )}
                        </div>
                    )}

                    {/* Result */}
                    {step === "result" && bestProgram && (
                        <div className="text-center" data-testid="quiz-result">
                            <div className="text-[11px] font-bold text-[#B36318] uppercase tracking-[0.2em] mb-2">
                                {isAr ? "نتيجتك" : "התוצאה שלך"}
                            </div>
                            <div
                                className="w-24 h-24 mx-auto rounded-3xl flex items-center justify-center mb-4 shadow-[0_15px_35px_rgba(212,118,31,0.4)]"
                                style={{ background: `linear-gradient(135deg, ${bestProgram.color}, ${bestProgram.color}CC)` }}
                            >
                                <ResultIcon size={44} className="text-white" />
                            </div>
                            <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#0B1F3D] mb-2">
                                {isAr ? "المسار الأنسب لك:" : "המסלול המתאים לך ביותר:"}
                            </h3>
                            <div className="font-serif text-3xl md:text-4xl font-bold mb-3" style={{ color: bestProgram.color }}>
                                {isAr ? bestProgram.nameAr : bestProgram.nameHe}
                            </div>
                            <p className="text-[#475569] max-w-md mx-auto mb-6 text-sm md:text-base leading-relaxed">
                                {isAr ? bestProgram.descAr : bestProgram.descHe}
                            </p>

                            {/* Top 3 */}
                            <div className="bg-slate-50 rounded-2xl p-5 mb-6 max-w-md mx-auto">
                                <div className="text-[10px] font-bold text-[#475569] uppercase tracking-[0.2em] mb-3 text-start">
                                    {isAr ? "أفضل 3 مسارات لك" : "3 המסלולים המובילים שלך"}
                                </div>
                                <div className="space-y-2">
                                    {top3.map((p, idx) => (
                                        <div key={p.id} className="flex items-center gap-3 text-start">
                                            <div className="w-7 h-7 rounded-full text-white text-xs font-bold flex items-center justify-center shrink-0" style={{ background: p.color }}>
                                                {idx + 1}
                                            </div>
                                            <div className="flex-1 text-sm font-semibold text-[#334155]">{isAr ? p.nameAr : p.nameHe}</div>
                                            <div className="text-xs font-bold" style={{ color: p.color }}>{p.score} {isAr ? "نقطة" : "נקודות"}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                                <button
                                    onClick={() => setStep("form")}
                                    data-testid="quiz-cta-consult"
                                    className="inline-flex items-center gap-2 bg-gradient-to-r from-[#D4761F] to-[#E08A30] hover:from-[#B36318] hover:to-[#D4761F] text-white px-6 py-3 rounded-xl font-bold shadow-[0_10px_30px_rgba(212,118,31,0.4)] transition-all hover:scale-105"
                                >
                                    <Sparkles size={16} />
                                    {isAr ? "أريد استشارة شخصية" : "רוצה ייעוץ אישי"}
                                </button>
                                <button
                                    onClick={handleReset}
                                    className="inline-flex items-center gap-1.5 text-[#475569] hover:text-[#0B1F3D] font-semibold text-sm"
                                >
                                    <RotateCcw size={14} />
                                    {isAr ? "أعد الاختبار" : "בצע שוב"}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Lead form */}
                    {step === "form" && bestProgram && (
                        <form onSubmit={handleSubmitLead} className="max-w-md mx-auto" data-testid="quiz-lead-form">
                            <h3 className="font-serif text-2xl font-bold text-[#0B1F3D] mb-2 text-center">
                                {isAr ? "احصل على استشارة شخصية" : "קבל/י ייעוץ אישי"}
                            </h3>
                            <p className="text-[#475569] text-sm text-center mb-6">
                                {isAr
                                    ? `مستشار الأهلية سيتواصل معك خلال 24 ساعة لمناقشة مسار ${bestProgram.nameAr}.`
                                    : `יועץ אל-אהלייה יצור אתך קשר תוך 24 שעות לדבר על מסלול ${bestProgram.nameHe}.`}
                            </p>

                            <div className="space-y-4">
                                <div>
                                    <label className="block text-xs font-bold text-[#475569] mb-1.5">
                                        {isAr ? "الاسم الكامل *" : "שם מלא *"}
                                    </label>
                                    <div className="relative">
                                        <User size={16} className="absolute top-1/2 -translate-y-1/2 start-3 text-[#94A3B8]" />
                                        <input
                                            type="text"
                                            value={leadForm.fullName}
                                            onChange={(e) => setLeadForm({ ...leadForm, fullName: e.target.value })}
                                            required
                                            data-testid="quiz-form-name"
                                            className="w-full ps-10 pe-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D4761F] focus:bg-white transition"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-[#475569] mb-1.5">
                                        {isAr ? "هاتف *" : "טלפון *"}
                                    </label>
                                    <div className="relative">
                                        <Phone size={16} className="absolute top-1/2 -translate-y-1/2 start-3 text-[#94A3B8]" />
                                        <input
                                            type="tel"
                                            value={leadForm.phone}
                                            onChange={(e) => setLeadForm({ ...leadForm, phone: e.target.value })}
                                            required
                                            data-testid="quiz-form-phone"
                                            className="w-full ps-10 pe-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#D4761F] focus:bg-white transition"
                                        />
                                    </div>
                                </div>
                            </div>

                            <button
                                type="submit"
                                disabled={submitting}
                                data-testid="quiz-form-submit"
                                className="w-full mt-6 inline-flex items-center justify-center gap-2 bg-gradient-to-r from-[#D4761F] to-[#E08A30] hover:from-[#B36318] hover:to-[#D4761F] disabled:opacity-50 text-white py-3 rounded-xl font-bold shadow-[0_10px_30px_rgba(212,118,31,0.4)] transition-all"
                            >
                                <Send size={16} />
                                {submitting ? (isAr ? "جارٍ الإرسال..." : "שולח...") : (isAr ? "أرسل تفاصيلي" : "שלח פרטים")}
                            </button>
                        </form>
                    )}

                    {/* Done */}
                    {step === "done" && (
                        <div className="text-center py-10" data-testid="quiz-done">
                            <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[#22C55E] to-[#16A34A] flex items-center justify-center mb-4 shadow-[0_15px_35px_rgba(34,197,94,0.4)]">
                                <CheckCircle2 size={40} className="text-white" />
                            </div>
                            <h3 className="font-serif text-2xl font-bold text-[#0B1F3D] mb-2">
                                {isAr ? "تم استلام تفاصيلك!" : "הפרטים התקבלו!"}
                            </h3>
                            <p className="text-[#475569] max-w-sm mx-auto text-sm">
                                {isAr ? "سنتصل بك خلال 24 ساعة. شكرًا لثقتك بالكلية الأهلية." : "ניצור אתך קשר תוך 24 שעות. תודה על האמון באל-אהלייה."}
                            </p>
                            <button
                                onClick={onClose}
                                className="mt-6 bg-[#0B1F3D] hover:bg-[#1A365D] text-white px-6 py-2.5 rounded-lg font-semibold text-sm transition"
                            >
                                {isAr ? "إغلاق" : "סגור"}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
